import { CardType } from '@/types';

import BiweeklyCard from './BiweeklyCard';
import ChemistryCallCard from './ChemistryCallCard';
import HomeCard from './HomeCard';

import type { MemberCoachBookingAuthority } from '@/features/appointments/memberCoachBookingState';
import type { DecodedBrowserAppointment } from '@/features/appointments/types';
import type { HomeCardProps, HomeCardPropsWithoutCallbacks } from '@/types';
import type { FC } from 'react';

import { coachIdentityQueryArg } from '@/features/appointments/coachIdentity';
import {
  legacySubjectPersonId,
  resolveMemberCoachBookingAuthority,
  resolveMemberCoachBookingState,
  resolveQueryAuthorityState,
} from '@/features/appointments/memberCoachBookingState';
import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { useGetMomentsFeatureFlagForLegacySubjectQuery } from '@/store/api/momentsApi';

export const isMomentsScheduledCoach = (card: HomeCardPropsWithoutCallbacks) =>
  !!card.coach &&
  'coachProfile' in card.coach &&
  usesMomentsScheduling(card.coach);

export const isBookableCoachCard = (card: HomeCardPropsWithoutCallbacks) =>
  (card.type === CardType.MEET_COACH ||
    card.type === CardType.PREVIOUS_SESSION) &&
  !!card.coach?.id;

/**
 * Decision table for MEET_COACH card rendering (per coach):
 *
 * | scheduling provider | dashboard card type | render            |
 * | ------------------- | ------------------- | ----------------- |
 * | non-Moments         | any                 | legacy HomeCard   |
 * | Moments             | MEET_COACH          | ChemistryCallCard |
 * | Moments             | PREVIOUS_SESSION    | BiweeklyCard      |
 *
 * The `variant` prop selects which render location is asking. The full-width
 * location (CoachBookingCards, directly under the greeting) and the in-carousel
 * location (Cards) call the same gate; only the matched cell renders, the others
 * return null.
 */
export const MeetCoachCardGate: FC<
  HomeCardProps & {
    appointments: DecodedBrowserAppointment[];
    appointmentState: MemberCoachBookingAuthority['appointmentState'];
    card: HomeCardPropsWithoutCallbacks;
    matchState: MemberCoachBookingAuthority['matchState'];
    now: number;
    variant: 'biweekly' | 'carousel' | 'chemistry';
  }
> = ({
  appointments,
  appointmentState,
  card,
  matchState,
  now,
  variant,
  ...homeCardProps
}) => {
  const nativeSchedulingEnabled = isMomentsScheduledCoach(card);
  const {
    currentData: coachFlag,
    isError: isCoachIdentityError,
    isFetching: isCoachIdentityFetching,
    isSuccess: isCoachIdentitySuccess,
  } = useGetMomentsFeatureFlagForLegacySubjectQuery(
    coachIdentityQueryArg(card.coach?.id),
  );
  const coachPersonId = legacySubjectPersonId(coachFlag) ?? undefined;
  const identityState = resolveQueryAuthorityState({
    hasCurrentData: !!coachPersonId,
    isError: isCoachIdentityError,
    isFetching: isCoachIdentityFetching,
    isSuccess: isCoachIdentitySuccess,
  });
  const { canBookBiweekly } = resolveMemberCoachBookingAuthority(
    {
      appointmentState,
      appointments,
      coachPersonId: coachPersonId ?? null,
      identityState,
      matchState,
    },
    now,
  );
  const bookingState = resolveMemberCoachBookingState({
    appointments,
    coachPersonId,
    hasAuthoritativeAppointments: appointmentState === 'ready',
    isCoachIdentityFetching,
    legacyState:
      card.type === CardType.PREVIOUS_SESSION ? 'has_previous' : 'no_previous',
    now,
  });

  if (nativeSchedulingEnabled && appointmentState !== 'ready') {
    return null;
  }

  if (
    nativeSchedulingEnabled &&
    (bookingState === 'upcoming_chemistry' ||
      bookingState === 'active_biweekly')
  ) {
    return null;
  }

  if (variant === 'chemistry') {
    return nativeSchedulingEnabled && bookingState === 'no_previous' ? (
      <ChemistryCallCard card={{ ...card, type: CardType.MEET_COACH }} />
    ) : null;
  }

  if (variant === 'biweekly') {
    return nativeSchedulingEnabled &&
      bookingState === 'has_previous' &&
      canBookBiweekly ? (
      <BiweeklyCard card={card} />
    ) : null;
  }

  // Carousel variant: preserve the existing scheduler for non-Moments coaches.
  return nativeSchedulingEnabled ? null : <HomeCard {...homeCardProps} />;
};
